import { Accordion, Group, Title, Badge } from '@mantine/core';
import { IconMap, IconTools, IconBlocks } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import classes from './field-navbar.module.css';

import { useAuth } from '@auth/data-access';
import { hasAdminAccess } from '@data-access';
import { FieldAdvices, MAP_TYPES } from '@fields/data-access';
import { TsrNavLink } from '@ui';

interface FieldNavbarProps {
  fieldId: string;
  links: { type: MAP_TYPES; numbers: number | null }[];
  disabled?: boolean;
  advices: FieldAdvices;
}

export function FieldNavbar({
  fieldId,
  links,
  disabled = false,
  advices,
}: FieldNavbarProps) {
  const { t } = useTranslation();
  const { user } = useAuth();

  const type = advices?.sunflower ? 'sunflower' : 'camelina';
  const formId = advices?.[type] ?? null;

  return (
    <Accordion
      classNames={{
        item: classes.navbarItem,
      }}
      defaultValue={disabled ? [] : ['maps', 'advices']}
      multiple
    >
      <Accordion.Item value={'maps'}>
        <Accordion.Control disabled={disabled}>
          <Group>
            <IconMap />
            <Title order={3}>{t('field.nav.maps')}</Title>
          </Group>
        </Accordion.Control>
        <Accordion.Panel>
          {links
            .filter(
              (link) =>
                !(
                  (link.type === MAP_TYPES.POTENTIAL ||
                    link.type === MAP_TYPES.YIELD) &&
                  !hasAdminAccess(user?.user_access)
                ),
            )
            .map((link) => (
              <TsrNavLink
                classNames={{
                  label: classes.navLinkLabel,
                }}
                disabled={link.numbers === -1}
                key={link.type}
                label={
                  <Group justify="space-between" wrap="nowrap">
                    {t(`field.maps.${link.type}`)}
                    {link.numbers !== null ? (
                      <Badge
                        className={classes.badge}
                        radius="sm"
                        variant="outline"
                      >
                        {link.numbers === -1 ? '0' : link.numbers}
                      </Badge>
                    ) : null}
                  </Group>
                }
                params={{ fieldId, mapType: link.type }}
                to={`/fields/${fieldId}/maps/${link.type}`}
              />
            ))}
        </Accordion.Panel>
      </Accordion.Item>

      <Accordion.Item value={'tools'}>
        <Accordion.Control disabled={disabled}>
          <Group>
            <IconTools />
            <Title order={3}>{t('field.nav.tools')}</Title>
          </Group>
        </Accordion.Control>
        <Accordion.Panel>
          <TsrNavLink
            label={t('field.nav.cut')}
            params={{ fieldId }}
            to={`/fields/$fieldId/cut`}
          />
          <TsrNavLink
            disabled={!hasAdminAccess(user?.user_access)}
            label={t('field.nav.compare')}
            params={{ fieldId, mapType: MAP_TYPES.COMPARE }}
            to={`/fields/$fieldId/maps/$mapType`}
          />
        </Accordion.Panel>
      </Accordion.Item>

      <Accordion.Item value={'advices'}>
        <Accordion.Control disabled={disabled}>
          <Group>
            <IconBlocks />
            <Title order={3}>{t('field.nav.advices')}</Title>
          </Group>
        </Accordion.Control>
        <Accordion.Panel>
          <TsrNavLink
            disabled={formId === null}
            label={t('field.nav.interculture')}
            search={{
              selectedForm: formId ?? undefined,
              editMode: true,
              type: type,
            }}
            to="/interculture"
          />
          <TsrNavLink
            disabled={!advices.insurance || !hasAdminAccess(user?.user_access)}
            label={t('field.nav.insurance')}
            search={{
              id: advices.insurance,
            }}
            to="/insurance"
          />
        </Accordion.Panel>
      </Accordion.Item>
    </Accordion>
  );
}

export default FieldNavbar;
